import {
  LivenessWebVerifyStatusEnum,
  RegtankLivenessFHStatusEnum,
  RegtankOnboardingFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import { RegtankOnboardingStatusEnum } from "@funded-here-interface/common/src/constant/enum";
import { sortRegtankRequestsByExpiry } from "@funded-here-interface/common/src/Utils/regtankOnboardingHelper";

export interface resultOnboardingChecking {
  matchRequestId: string;
  newStatus: RegtankOnboardingFHStatusEnum;
  disabledPhotoVidBtn: boolean;
  disabledDocumentBtn: boolean;
  hiddenVerificationHistoryData: boolean;
  newVerificationHistoryStatus: string;
  newReviewedOn: string;
  newSubmittedOn: string;
  newFailureReason: string;
  newReattemptApproval: string;
  newRemark: string;
  expiredDate: string;
  documentIdSubmittedDate: string;
}

export interface resultLivenessChecking {
  matchSessionId: string;
  newStatus: RegtankLivenessFHStatusEnum;
  hiddenLivenessHistoryData: boolean;
  newVerificationResult: string;
  disabledPhotoVidBtn: boolean;
  newReviewedOn: string;
  newFailureReason: string;
  newRemark: string;
  newReattemptApproval: string;
  newSubmittedOn: string;
  expiredDate: string;
}

export interface rawOnboardingData {
  requestId: string;
  onboardingStatus: string;
  expiredDate: string;
  reattemptApproval: string;
  submittedOn?: string;
  reviewedOn?: string;
  remark?: string;
  reason?: string;
  documentIdSubmittedDate?: string;
}

interface reattemptApprovalData {
  requestId: string;
  reattemptApproval: string;
  onboardingStatus: string;
  reviewedOn?: string;
  submittedOn?: string;
  reason?: string;
  remark?: string;
  documentIdSubmittedDate?: string;
  expiredDate: string;
}

export interface rawLivenessData {
  sessionId: string;
  livenessStatus: LivenessWebVerifyStatusEnum;
  expiredDate: string;
  numberOfLiveness: number;
  reattemptApproval: string;
  submittedOn: string;
  failureReason: string;
}

interface livenessReattemptData {
  sessionId: string;
  reattemptApproval: string;
  submittedOn: string;
  failureReason: string;
  livenessStatus: LivenessWebVerifyStatusEnum;
  expiredDate: string;
}

// Constants for common default values
const DASH = "-";
const EMPTY_STRING = "";

// Common field sets for status results
const PASSED_DEFAULTS = {
  newReviewedOn: DASH,
  newFailureReason: DASH,
  newRemark: DASH,
  newReattemptApproval: DASH,
  newVerificationResult: "PASSED",
};

export const getRegtankColor = (
  status: RegtankOnboardingFHStatusEnum | RegtankLivenessFHStatusEnum
): string => {
  switch (status) {
    case RegtankOnboardingFHStatusEnum.PASSED:
    case RegtankLivenessFHStatusEnum.PASSED:
      return "green";
    case RegtankOnboardingFHStatusEnum.PENDING_REVIEW:
      return "yellow";
    case RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT:
    case RegtankLivenessFHStatusEnum.PENDING_REATTEMPT:
      return "blue";
    case RegtankOnboardingFHStatusEnum.REATTEMPT_REJECTED:
    case RegtankLivenessFHStatusEnum.REATTEMPT_REJECTED:
      return "red";
    case RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION:
    case RegtankLivenessFHStatusEnum.NOTHING:
      return "gray";
    default:
      return "gray";
  }
};

/**
 * Helper to create base result object with default values
 */
const createBaseOnboardingResult = (
  requestId: string,
  expiredDate: string,
  submittedOn?: string,
  reason?: string,
  documentIdSubmittedDate?: string
): resultOnboardingChecking => ({
  matchRequestId: requestId,
  newStatus: RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION,
  disabledPhotoVidBtn: true,
  disabledDocumentBtn: true,
  hiddenVerificationHistoryData: false,
  newVerificationHistoryStatus: EMPTY_STRING,
  newReviewedOn: EMPTY_STRING,
  newSubmittedOn: submittedOn || EMPTY_STRING,
  newFailureReason: reason || EMPTY_STRING,
  newReattemptApproval: EMPTY_STRING,
  newRemark: EMPTY_STRING,
  expiredDate,
  documentIdSubmittedDate: documentIdSubmittedDate || EMPTY_STRING,
});

/**
 * Handle reattempt approval logic (APPROVED/REJECTED)
 */
const handleReattemptApproval = (
  reattemptApprovalData: reattemptApprovalData[]
): resultOnboardingChecking[] | null => {
  const result: resultOnboardingChecking[] = [];
  for (const data of reattemptApprovalData) {
    if (data.onboardingStatus === RegtankOnboardingStatusEnum.APPROVED) {
      continue; // Skip reattempt logic if already approved
    }

    const baseResult = createBaseOnboardingResult(
      data.requestId,
      data.expiredDate,
      data.submittedOn
    );
    if (data.reattemptApproval === "APPROVED") {
      result.push({
        ...baseResult,
        newStatus: RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
        newReviewedOn: data.reviewedOn || EMPTY_STRING,
        newSubmittedOn: data.submittedOn || EMPTY_STRING,
        newFailureReason: data.reason || EMPTY_STRING,
        newReattemptApproval: data.reattemptApproval,
        newRemark: data.remark || EMPTY_STRING,
        newVerificationHistoryStatus: data.submittedOn ? "FAILED" : "EXPIRED",
        disabledDocumentBtn: data.documentIdSubmittedDate ? false : true,
        disabledPhotoVidBtn: data.submittedOn ? false : true,
      });
    } else if (data.reattemptApproval === "REJECTED") {
      result.push({
        ...baseResult,
        newStatus: RegtankOnboardingFHStatusEnum.REATTEMPT_REJECTED,
        newVerificationHistoryStatus: data.submittedOn ? "FAILED" : "EXPIRED",
        disabledDocumentBtn: data.documentIdSubmittedDate ? false : true,
        disabledPhotoVidBtn: data.submittedOn ? false : true,
        newFailureReason: data.reason || EMPTY_STRING,
      });
    }
  }

  return result;
};

/**
 * Process status for APPROVED cases
 */
const handleApprovedStatus = (): Partial<resultOnboardingChecking> => ({
  newStatus: RegtankOnboardingFHStatusEnum.PASSED,
  newVerificationHistoryStatus: "PASSED",
  disabledPhotoVidBtn: false,
  disabledDocumentBtn: false,
  ...PASSED_DEFAULTS,
});

/**
 * Process status for REJECTED cases
 */
const handleRejectedStatus = (): Partial<resultOnboardingChecking> => ({
  newStatus: RegtankOnboardingFHStatusEnum.PENDING_REVIEW,
  newVerificationHistoryStatus: "FAILED",
  disabledDocumentBtn: false,
  disabledPhotoVidBtn: false,
});

/**
 * Process status for EXPIRED cases (ID_UPLOADED, LIVENESS_STARTED)
 */
const handleExpiredStatus = (
  disabledDocumentBtn: boolean,
  disabledPhotoVidBtn: boolean
): Partial<resultOnboardingChecking> => ({
  newStatus: RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
  newVerificationHistoryStatus: "EXPIRED",
  newSubmittedOn: DASH,
  disabledDocumentBtn,
  disabledPhotoVidBtn,
  ...PASSED_DEFAULTS,
});

/**
 * Process status changes based on onboarding status
 */
const processStatusChange = (
  onboardingStatus: string,
  isBeforeExpiry: boolean,
  documentIdSubmittedDate?: string,
  submittedDate?: string
): Partial<resultOnboardingChecking> => {
  const disabledDocumentBtn = !documentIdSubmittedDate;
  const disabledPhotoVidBtn = !submittedDate;
  switch (onboardingStatus) {
    case RegtankOnboardingStatusEnum.APPROVED:
      return handleApprovedStatus();

    case RegtankOnboardingStatusEnum.REJECTED:
      return handleRejectedStatus();

    case RegtankOnboardingStatusEnum.EXPIRED:
      return disabledDocumentBtn === false
        ? {
            disabledDocumentBtn,
            newSubmittedOn: DASH,
            newReviewedOn: DASH,
            disabledPhotoVidBtn,
            newVerificationHistoryStatus: "EXPIRED",
            newFailureReason: DASH,
            newRemark: DASH,
            newReattemptApproval: DASH,
          }
        : {
            hiddenVerificationHistoryData: true,
          };
    case RegtankOnboardingStatusEnum.PROCESSING:
      if (!isBeforeExpiry) {
        //ALREADY EXPIRED
        return {
          hiddenVerificationHistoryData: true,
          disabledDocumentBtn,
        };
      }
      return isBeforeExpiry
        ? { disabledDocumentBtn }
        : { hiddenVerificationHistoryData: true, disabledDocumentBtn };

    case RegtankOnboardingStatusEnum.ID_UPLOADED:
    case RegtankOnboardingStatusEnum.LIVENESS_STARTED:
      return !isBeforeExpiry
        ? handleExpiredStatus(disabledDocumentBtn, disabledPhotoVidBtn)
        : {
            disabledDocumentBtn,
            disabledPhotoVidBtn,
            newSubmittedOn: EMPTY_STRING,
          };

    case RegtankOnboardingStatusEnum.ID_UPLOADED_FAILED:
      return !isBeforeExpiry
        ? {
            newStatus: RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
            hiddenVerificationHistoryData: true,
            disabledDocumentBtn,
          }
        : {};

    case RegtankOnboardingStatusEnum.LIVENESS_FAILED:
      if (isBeforeExpiry) {
        return {
          newSubmittedOn: EMPTY_STRING,
          disabledDocumentBtn,
          disabledPhotoVidBtn: true,
          newFailureReason: EMPTY_STRING,
        };
      }
      return {
        newStatus: RegtankOnboardingFHStatusEnum.PENDING_REVIEW,
        disabledPhotoVidBtn,
        disabledDocumentBtn,
        newVerificationHistoryStatus: "FAILED",
      };

    default:
      return {};
  }
};

export const processOnboardingData = (
  onboardingDataArray: rawOnboardingData[]
): resultOnboardingChecking[] => {
  let results: resultOnboardingChecking[] = [];
  // Handle reattempt approval first
  let reattemptApprovalData: reattemptApprovalData[] = [];
  for (const data of onboardingDataArray) {
    reattemptApprovalData.push({
      requestId: data.requestId,
      reattemptApproval: data.reattemptApproval,
      onboardingStatus: data.onboardingStatus,
      reviewedOn: data.reviewedOn,
      reason: data.reason,
      remark: data.remark,
      documentIdSubmittedDate: data.documentIdSubmittedDate,
      expiredDate: data.expiredDate,
      submittedOn: data.submittedOn,
    });
  }
  const reattemptResult = handleReattemptApproval(reattemptApprovalData);
  if (reattemptResult) {
    results = reattemptResult;
  }
  // Create base result
  for (const data of onboardingDataArray) {
    if (data.reattemptApproval) {
      continue;
    }
    const baseResult = createBaseOnboardingResult(
      data.requestId,
      data.expiredDate,
      data.submittedOn,
      data.reason
    );

    // Check if before expiry
    const isBeforeExpiry = data.expiredDate >= new Date().toISOString();
    // Process status changes
    const statusChanges = processStatusChange(
      data.onboardingStatus,
      isBeforeExpiry,
      data.documentIdSubmittedDate,
      data.submittedOn
    );

    const processedResult = {
      ...baseResult,
      ...statusChanges,
    };

    results.push(processedResult);
  }
  const sortedResults = sortRegtankRequestsByExpiry(results);
  return sortedResults;
};

/**
 * Generic reattempt approval handler for both onboarding and liveness
 */
const handleGenericReattemptApproval = <T extends string>(
  reattemptApproval: string,
  verificationResult: string,
  failedStatuses: string[],
  pendingReattemptStatus: T,
  reattemptRejectedStatus: T,
  defaultStatus: T
): T => {
  const isFailed = failedStatuses.includes(verificationResult);

  if (reattemptApproval === "APPROVED" && isFailed) {
    return pendingReattemptStatus;
  }
  if (reattemptApproval === "REJECTED" && isFailed) {
    return reattemptRejectedStatus;
  }
  return defaultStatus;
};

export const changeStatusAfterReattemptApproval = (data: {
  reattemptApproval: string;
  verificationResult: string;
}): RegtankOnboardingFHStatusEnum => {
  return handleGenericReattemptApproval(
    data.reattemptApproval,
    data.verificationResult,
    [
      RegtankOnboardingStatusEnum.REJECTED,
      RegtankOnboardingStatusEnum.LIVENESS_FAILED,
    ],
    RegtankOnboardingFHStatusEnum.PENDING_REATTEMPT,
    RegtankOnboardingFHStatusEnum.REATTEMPT_REJECTED,
    RegtankOnboardingFHStatusEnum.PENDING_REVIEW
  );
};

/**
 * Helper to create base liveness result
 */
const createBaseLivenessResult = (
  sessionId: string,
  expiredDate: string
): resultLivenessChecking => ({
  matchSessionId: sessionId,
  newStatus: RegtankLivenessFHStatusEnum.NOTHING,
  hiddenLivenessHistoryData: false,
  newVerificationResult: EMPTY_STRING,
  disabledPhotoVidBtn: true,
  newReviewedOn: EMPTY_STRING,
  newFailureReason: EMPTY_STRING,
  newRemark: EMPTY_STRING,
  newReattemptApproval: EMPTY_STRING,
  newSubmittedOn: EMPTY_STRING,
  expiredDate,
});

/**
 * Handle liveness reattempt approval
 */
const handleLivenessReattemptApproval = (
  livenessReattemptData: livenessReattemptData[]
): resultLivenessChecking[] | null => {
  let result: resultLivenessChecking[] = [];

  for (const data of livenessReattemptData) {
    if (data.livenessStatus === LivenessWebVerifyStatusEnum.SUCCESS) {
      continue;
    }

    const baseResult = createBaseLivenessResult(
      data.sessionId,
      data.expiredDate
    );

    if (data.reattemptApproval === "APPROVED") {
      result.push({
        ...baseResult,
        newStatus: RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
        newVerificationResult: "FAILED",
        disabledPhotoVidBtn: false,
        newFailureReason: data.failureReason,
        newReattemptApproval: data.reattemptApproval,
        newSubmittedOn: data.submittedOn,
      });
    } else if (data.reattemptApproval === "REJECTED") {
      result.push({
        ...baseResult,
        newStatus: RegtankLivenessFHStatusEnum.REATTEMPT_REJECTED,
        newVerificationResult: "FAILED",
        disabledPhotoVidBtn: false,
        newFailureReason: data.failureReason,
        newReattemptApproval: data.reattemptApproval,
        newSubmittedOn: data.submittedOn,
      });
    }
  }
  return result;
};

/**
 * Process liveness status changes
 */
const processLivenessStatus = (
  livenessStatus: LivenessWebVerifyStatusEnum | null,
  isNotExpired: boolean,
  numberOfLiveness: number,
  submittedOn: string,
  failureReason: string,
  numberOfAttempts: number
): Partial<resultLivenessChecking> => {
  // Handle null or LIVENESS_FAILED before expiry
  if (
    isNotExpired &&
    (livenessStatus === null ||
      livenessStatus === LivenessWebVerifyStatusEnum.LIVENESS_FAILED)
  ) {
    return numberOfAttempts >= 1
      ? {
          newStatus: RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
          newVerificationResult: EMPTY_STRING,
          newSubmittedOn: EMPTY_STRING,
        }
      : {
          newStatus: RegtankLivenessFHStatusEnum.NOTHING,
          newVerificationResult: EMPTY_STRING,
          newSubmittedOn: EMPTY_STRING,
        };
  }

  // Handle null after expiry
  if (!isNotExpired && livenessStatus === null) {
    return numberOfLiveness > 0
      ? {
          newStatus: RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
          hiddenLivenessHistoryData: true,
        }
      : { hiddenLivenessHistoryData: true };
  }

  // Handle specific liveness statuses
  switch (livenessStatus) {
    case LivenessWebVerifyStatusEnum.SUCCESS:
      return {
        newStatus: RegtankLivenessFHStatusEnum.PASSED,
        disabledPhotoVidBtn: false,
        newSubmittedOn: submittedOn,
        ...PASSED_DEFAULTS,
      };

    case LivenessWebVerifyStatusEnum.FAILED:
      return {
        newStatus: RegtankLivenessFHStatusEnum.PENDING_REVIEW,
        disabledPhotoVidBtn: false,
        newSubmittedOn: submittedOn,
        newFailureReason: failureReason,
        newVerificationResult: "FAILED",
      };

    case LivenessWebVerifyStatusEnum.LIVENESS_FAILED:
      return !isNotExpired
        ? {
            newStatus: RegtankLivenessFHStatusEnum.PENDING_REVIEW,
            newVerificationResult: "FAILED",
            disabledPhotoVidBtn: false,
            newFailureReason: failureReason,
          }
        : {};

    default:
      return {};
  }
};

export const processLivenessData = (
  livenessDataArray: rawLivenessData[]
): resultLivenessChecking[] => {
  let results: resultLivenessChecking[] = [];
  // Handle reattempt approval first
  let reattemptApprovalData: livenessReattemptData[] = [];
  for (const data of livenessDataArray) {
    reattemptApprovalData.push({
      sessionId: data.sessionId,
      reattemptApproval: data.reattemptApproval,
      submittedOn: data.submittedOn,
      failureReason: data.failureReason,
      livenessStatus: data.livenessStatus,
      expiredDate: data.expiredDate,
    });
  }
  const reattemptResult = handleLivenessReattemptApproval(
    reattemptApprovalData
  );
  if (reattemptResult) {
    results = reattemptResult;
  }

  // Create base result
  for (const data of livenessDataArray) {
    if (data.reattemptApproval) {
      continue;
    }
    const baseResult = createBaseLivenessResult(
      data.sessionId,
      data.expiredDate
    );
    baseResult.newSubmittedOn = data.submittedOn;
    // Check if not expired
    const isNotExpired = data.expiredDate >= new Date().toISOString();
    // Process liveness status
    const statusChanges = processLivenessStatus(
      data.livenessStatus,
      isNotExpired,
      data.numberOfLiveness,
      data.submittedOn,
      data.failureReason,
      livenessDataArray.length - 1
    );
    const processedResult = {
      ...baseResult,
      ...statusChanges,
    };
    results.push(processedResult);
  }
  const sortedResults = sortRegtankRequestsByExpiry(results);
  return sortedResults;
};

export const changeStatusLivenessReattemptApproval = (data: {
  reattemptApproval: string;
  verificationResult: string;
}): RegtankLivenessFHStatusEnum => {
  return handleGenericReattemptApproval(
    data.reattemptApproval,
    data.verificationResult,
    [
      LivenessWebVerifyStatusEnum.FAILED,
      LivenessWebVerifyStatusEnum.LIVENESS_FAILED,
    ],
    RegtankLivenessFHStatusEnum.PENDING_REATTEMPT,
    RegtankLivenessFHStatusEnum.REATTEMPT_REJECTED,
    RegtankLivenessFHStatusEnum.NOTHING
  );
};
